import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";

export default function AuthDialog({ open, onOpenChange, initialMode = "login" }) {
  const { login, register } = useAuth();
  const nav = useNavigate();

  const [mode, setMode] = useState(initialMode);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Keep dialog mode in sync when re-opened from different CTAs
  function handleOpenChange(next) {
    if (!next) {
      setErr("");
    } else {
      setMode(initialMode);
    }
    onOpenChange(next);
  }

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (mode === "register") {
        await register(email, password, name);
      } else {
        await login(email, password);
      }
      onOpenChange(false);
      nav("/studio", { replace: true });
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally {
      setBusy(false);
    }
  }

  const isRegister = mode === "register";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-md p-0 overflow-hidden border-stone-200 gap-0"
        data-testid="auth-dialog"
      >
        <div className="p-7 sm:p-8" style={{ background: "#f7f5f0" }}>
          <div className="flex items-baseline justify-between mb-1">
            <h2 className="font-display text-3xl text-stone-900 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#C27A62]" />
              Atelier Studio
            </h2>
            <span className="text-[10px] uppercase tracking-[0.18em] text-stone-500">
              {isRegister ? "Free trial" : "Sign in"}
            </span>
          </div>
          <p className="text-sm text-stone-500">
            {isRegister
              ? "Create your studio — 28 days free, no card required."
              : "Welcome back. Sign in to continue your projects."}
          </p>
        </div>

        <form onSubmit={onSubmit} className="p-7 sm:p-8 space-y-4 bg-white">
          {isRegister && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
                Name
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="auth-name-input"
                placeholder="Your studio or name"
                className="h-11"
              />
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
              Email
            </label>
            <Input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="auth-email-input"
              placeholder="you@studio.com"
              className="h-11"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold block mb-1.5">
              Password
            </label>
            <Input
              type="password"
              autoComplete={isRegister ? "new-password" : "current-password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="auth-password-input"
              placeholder="••••••••"
              className="h-11"
            />
            {isRegister && (
              <p className="text-xs text-stone-400 mt-1">At least 8 characters.</p>
            )}
          </div>

          {err && (
            <div
              className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2"
              data-testid="auth-error"
            >
              {err}
            </div>
          )}

          <Button
            type="submit"
            disabled={busy}
            data-testid="auth-submit-button"
            className="w-full h-11 rounded-full bg-stone-900 text-white hover:bg-stone-800"
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} />
                {isRegister ? "Creating…" : "Signing in…"}
              </>
            ) : isRegister ? (
              "Start free trial"
            ) : (
              "Sign in"
            )}
          </Button>

          <p className="text-xs text-stone-500 text-center">
            {isRegister ? (
              <>
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => {
                    setErr("");
                    setMode("login");
                  }}
                  className="text-stone-900 underline underline-offset-2"
                  data-testid="auth-switch-login"
                >
                  Sign in
                </button>
              </>
            ) : (
              <>
                New to Atelier Studio?{" "}
                <button
                  type="button"
                  onClick={() => {
                    setErr("");
                    setMode("register");
                  }}
                  className="text-stone-900 underline underline-offset-2"
                  data-testid="auth-switch-register"
                >
                  Start a free trial
                </button>
              </>
            )}
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
